import React, { useCallback, useMemo, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import { useAppStore } from '../../store/useAppStore'
import { uploadService } from '../../services/upload.service'
import {
  captureOrganizationScope,
  showErrorIfSameOrganization,
  showSuccessIfSameOrganization,
} from '../../utils/orgScopedNotification'
import { buildInputAcceptString, getEffectiveAllowedMimes, isUploadAllowed } from '../../utils/uploadMimePolicy'
import './DocumentUpload.scss'

export const DocumentUpload: React.FC = () => {
  const { t } = useTranslation()
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const canAddDocument = permissionsService.currentUserCan(PermissionAction.Add, PermissionType.Document)
  const uiSettings = useAppStore((s) => s.uiSettings)

  const supportedUploadTypes = uiSettings?.settings?.supported_upload_types
  const allowedUploadMimes = uiSettings?.settings?.allowed_upload_mime_types
  const effectiveMimes = useMemo(
    () => getEffectiveAllowedMimes(supportedUploadTypes, allowedUploadMimes),
    [supportedUploadTypes, allowedUploadMimes],
  )
  const effectiveMimeSet = useMemo(() => new Set(effectiveMimes), [effectiveMimes])
  const inputAcceptHint = useMemo(
    () => buildInputAcceptString(effectiveMimes, supportedUploadTypes),
    [effectiveMimes, supportedUploadTypes],
  )

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!canAddDocument) return
      if (acceptedFiles.length === 0) return

      setUploading(true)
      let successCount = 0
      const uploadOrgId = captureOrganizationScope()

      for (const file of acceptedFiles) {
        try {
          await uploadService.uploadDocument(file, {}, (progressData) => {
            setProgress(progressData.percentage)
          })
          successCount++
          showSuccessIfSameOrganization(
            uploadOrgId,
            t('document_uploaded_successfully', { name: file.name }),
          )
        } catch (error) {
          showErrorIfSameOrganization(
            uploadOrgId,
            `${t('error_uploading_document')} "${file.name}"`,
            error,
          )
        }
      }

      setUploading(false)
      setProgress(0)

      if (successCount > 0) {
        showSuccessIfSameOrganization(
          uploadOrgId,
          t('documents_uploaded_successfully', { count: successCount }),
        )
      }
    },
    [canAddDocument, t]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    validator: (file) =>
      isUploadAllowed(file, effectiveMimeSet, supportedUploadTypes)
        ? null
        : { code: 'file-invalid-type', message: t('file_type_not_supported') },
    disabled: uploading || !canAddDocument,
  })

  if (!canAddDocument) {
    return null
  }

  return (
    <>
      <div className="upload-widget">
        <div className="upload-widget__header">
          <div className="upload-widget__icon">
            <i className="bi bi-cloud-upload"></i>
          </div>
          <h6 className="upload-widget__title">{t('upload_document')}</h6>
        </div>
        <div className="upload-widget__body">
          <div {...getRootProps()} className={`upload-widget__dropzone ${isDragActive ? 'active' : ''} ${uploading ? 'uploading' : ''}`}>
            <input {...getInputProps({ accept: inputAcceptHint || undefined })} />
            {uploading ? (
              <div className="upload-widget__uploading">
                <div className="upload-widget__spinner">
                  <div className="upload-widget__spinner-circle"></div>
                  <div className="upload-widget__spinner-circle"></div>
                  <div className="upload-widget__spinner-circle"></div>
                </div>
                <p className="upload-widget__progress-text">{t('uploading_progress', { progress })}</p>
                <div className="upload-widget__progress">
                  <div className="upload-widget__progress-bar" style={{ width: `${progress}%` }}></div>
                </div>
              </div>
            ) : isDragActive ? (
              <div className="upload-widget__dropzone-content">
                <div className="upload-widget__dropzone-icon active">
                  <i className="bi bi-cloud-upload-fill"></i>
                </div>
                <p className="upload-widget__dropzone-text">{t('drop_files_here')}</p>
              </div>
            ) : (
              <div className="upload-widget__dropzone-content">
                <div className="upload-widget__dropzone-icon">
                  <i className="bi bi-cloud-upload"></i>
                </div>
                <p className="upload-widget__dropzone-text">{t('drag_drop_files')}</p>
                <p className="upload-widget__dropzone-hint">{t('supported_file_types')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
